import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Footer } from "@/components/Footer";

const Stars = ({ n }) => {
  if (!n) return null;
  return (
    <span className="inline-flex gap-0.5">
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} size={12} className="fill-[var(--mirror-accent)] text-[var(--mirror-accent)]" />
      ))}
    </span>
  );
};

const ReviewCard = ({ r, i }) => (
  <motion.figure
    data-testid={`review-card-${r.id}`}
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.8, delay: 0.06 * (i % 3) }}
    className="rounded-2xl border border-[rgba(242,236,228,0.08)] p-7 flex flex-col"
  >
    <blockquote className="font-serif-display text-xl font-light italic leading-relaxed text-[var(--mirror-text)]">
      “{r.content}”
    </blockquote>
    <figcaption className="mt-6 flex items-center justify-between gap-3">
      <div>
        {r.anonymous ? (
          <>
            <span className="block text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)]">
              Before Mirror
            </span>
            <span className="text-[var(--mirror-accent)] text-sm mt-1 block">
              {r.archetype}
            </span>
          </>
        ) : (
          <span className="text-[var(--mirror-text-2)] text-sm">— {r.display_name}</span>
        )}
      </div>
      <Stars n={r.rating} />
    </figcaption>
  </motion.figure>
);

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => {});
  }, []);

  const enter = () => {
    if (user && user !== false) {
      navigate(user.onboarded ? "/begin" : "/onboarding");
    } else {
      navigate("/auth");
    }
  };

  return (
    <div className="relative">
      {/* ---------------- hero (always dark — sits on an image) ---------------- */}
      <section data-theme="dark" className="min-h-screen relative flex flex-col overflow-hidden bg-[#16151F]">
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1620239864470-0cc2051b0fad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(26,26,46,0.4) 0%, rgba(26,26,46,0.92) 70%, #1A1A2E 100%)",
          }}
          aria-hidden="true"
        />

        <header className="relative z-10 px-8 sm:px-12 py-8 flex items-center justify-between">
          <span className="text-xs tracking-[0.35em] uppercase text-[var(--mirror-text-2)]">
            Mirror
          </span>
          <button
            data-testid="landing-signin-link"
            onClick={() => navigate("/auth")}
            className="text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
          >
            Sign in
          </button>
        </header>

        <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="font-serif-display font-light text-4xl sm:text-6xl lg:text-7xl leading-tight tracking-tight max-w-3xl"
          >
            I am a mirror.
            <br />
            You are looking at yourself.
          </motion.h1>

          <motion.button
            data-testid="landing-enter-button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, delay: 1, ease: "easeOut" }}
            onClick={enter}
            className="mt-16 px-10 py-3 rounded-full border border-[rgba(242,236,228,0.2)] text-sm tracking-[0.2em] uppercase text-[var(--mirror-text)] hover:bg-[rgba(242,236,228,0.06)] transition-colors duration-700"
          >
            Look
          </motion.button>
        </main>

        <footer className="relative z-10 px-8 py-8 text-center">
          <p className="text-xs text-[var(--mirror-text-3)] tracking-wide">
            This is not therapy. This is a conversation with your self.
          </p>
        </footer>
      </section>

      {/* ---------------- differentiator ---------------- */}
      <section
        data-testid="differentiator-section"
        className="relative px-6 sm:px-10 py-28 max-w-3xl mx-auto text-center"
      >
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1 }}
          className="font-serif-display font-light text-3xl sm:text-5xl tracking-tight leading-tight"
        >
          Most AI answers.
          <br />
          Mirror reflects.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mt-8 font-serif-display italic text-xl sm:text-2xl text-[var(--mirror-text-2)]"
        >
          Every answer you find will be your own.
        </motion.p>
      </section>

      {/* ---------------- how it works ---------------- */}
      <section
        data-testid="how-it-works-section"
        className="relative px-6 sm:px-10 pb-28 max-w-4xl mx-auto"
      >
        <h2 className="text-center text-xs tracking-[0.3em] uppercase text-[var(--mirror-text-3)]">
          How it works
        </h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
          {[
            { n: "01", t: "Ask what is on your mind." },
            { n: "02", t: "Mirror maps the belief beneath it." },
            { n: "03", t: "You leave with a question, not a verdict." },
          ].map((step, i) => (
            <motion.div
              key={step.n}
              data-testid={`how-step-${step.n}`}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: 0.15 * i }}
              className="text-center md:text-left"
            >
              <span className="font-serif-display text-4xl font-light text-[var(--mirror-accent)]">
                {step.n}
              </span>
              <p className="mt-4 font-serif-display text-xl font-light leading-relaxed text-[var(--mirror-text)]">
                {step.t}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------- the gnosis difference ---------------- */}
      <section
        data-testid="gnosis-difference-section"
        className="relative px-6 sm:px-10 pb-28 max-w-3xl mx-auto"
      >
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 1 }}
          className="rounded-2xl border border-[rgba(140,122,107,0.25)] bg-[rgba(140,122,107,0.05)] p-10 sm:p-14 text-center"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[var(--mirror-accent)]">
            The Gnosis Difference
          </p>
          <p className="mt-6 font-serif-display font-light text-xl sm:text-2xl leading-relaxed text-[var(--mirror-text)]">
            Mirror is grounded in the world's esoteric traditions: Gnostic, Hermetic, Sufi,
            Kabbalistic, and more, so your conversation draws from actual sources, not generic
            internet noise.
          </p>
        </motion.div>
      </section>

      {/* ---------------- reviews ---------------- */}
      {reviews.length > 0 && (
        <section
          data-testid="reviews-section"
          className="relative px-6 sm:px-10 py-28 max-w-5xl mx-auto"
        >
          <h2 className="font-serif-display font-light text-3xl sm:text-4xl tracking-tight text-center">
            Others who looked.
          </h2>
          <p className="mt-4 text-center text-sm text-[var(--mirror-text-3)] max-w-md mx-auto">
            Reflections from people who sat with the mirror, some named, some known only
            by who they were before.
          </p>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.map((r, i) => (
              <ReviewCard key={r.id} r={r} i={i} />
            ))}
          </div>

          <div className="mt-16 text-center">
            <button
              data-testid="reviews-cta-button"
              onClick={enter}
              className="px-10 py-3 rounded-full border border-[rgba(242,236,228,0.2)] text-sm tracking-[0.2em] uppercase hover:bg-[rgba(242,236,228,0.06)] transition-colors duration-700"
            >
              Look for yourself
            </button>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
